import React from "react"
import { axisLeft, Selection, EnterElement, ScaleBand } from "d3"
import { ReactCallbacks } from "../../../../Types/ReactCallbacks"
import { D3OneToOneRenderable } from "../../../D3/D3OneToOneRenderable"
import { EEGChannelDisplayInfo } from "../../../../Types/EEG"
import { getColors, MobergTheme } from "../../../../../../../../Moberg"
import { LABEL_SETTINGS } from "../../../../../../../../Constants/LabelSettings"

export type D3EEGYAxisConfig = {
	scale: ScaleBand<string>
	tickValues?: string[]
	channels?: EEGChannelDisplayInfo[]
	hiddenChannelKeys?: Set<string>
	onToggleChannel?: (channelKey: string) => void
	onContextMenu?: (event: React.MouseEvent, channelKey: string) => void
}

export class D3EEGYAxis extends D3OneToOneRenderable<SVGGElement, SVGGElement, D3EEGYAxisConfig> {
	private d3AxisClassName: string = "d3-axis-left"
	private hiddenColor = getColors(MobergTheme.GRAY).active
	private visibleColor = "var(--color-chart-axis-text, currentColor)"
	private ecgVisibleColor = LABEL_SETTINGS["ECG_II"].color

	constructor(root: SVGGElement, config: D3EEGYAxisConfig, reactCallbacks: ReactCallbacks<any>) {
		super(root, config, "d3-value-axis", reactCallbacks)
		this.render()
	}

	protected enter = (enterElements: Selection<EnterElement, any, any, any>): Selection<any, any, any, any> => {
		const axisGroup = enterElements
			.append("g")
			.attr("class", this.className)

		const d3Axis = axisGroup.append("g")
			.attr("class", this.d3AxisClassName)
			.call(this.getNewAxis())
			.style("user-select", "none")

		this.decorateTicks(d3Axis)

		return axisGroup
	}

	protected update = (updateElements: Selection<any, any, any, any>): Selection<any, any, any, any> => {
		const d3AxisLeft = updateElements.select("." + this.d3AxisClassName) as Selection<SVGGElement, any, any, any>
		d3AxisLeft.call(this.getNewAxis())
		this.decorateTicks(d3AxisLeft)
		return updateElements
	}

	private getNewAxis = () => {
		let scale = this.config.scale

		// If we provide tick values, just create a new scale for display purposes.
		if (this.config.tickValues) {
			scale = this.config.scale.copy().domain(this.config.tickValues)
		}

		return axisLeft(scale)
	}

	private decorateTicks = (axisGroup: Selection<SVGGElement, any, any, any>) => {
		const infoByKey = new Map((this.config.channels ?? []).map(channel => [channel.key, channel]))
		const hiddenKeys = this.config.hiddenChannelKeys ?? new Set<string>()
		const onToggle = this.config.onToggleChannel
		const onContextMenu = this.config.onContextMenu
		const isChannelClickable = (channelKey: string) => infoByKey.get(channelKey)?.spacer !== true

		axisGroup.selectAll<SVGGElement, string>(".tick")
			.filter(channelKey => infoByKey.get(channelKey)?.spacer === true)
			.remove()

		const ticks = axisGroup.selectAll<SVGGElement, string>(".tick")
			.style("cursor", channelKey => {
				if (!onToggle || !isChannelClickable(channelKey)) return "default"
				return "pointer"
			})

		if (onToggle) {
			ticks.on("click", (_, channelKey) => {
				if (!channelKey || !isChannelClickable(channelKey)) {
					return
				}

				onToggle(channelKey)
			})
		} else {
			ticks.on("click", null)
		}

		if (onContextMenu) {
			ticks.on("contextmenu", (event: React.MouseEvent, channelKey) => {
				event.preventDefault()
				if (!channelKey || !isChannelClickable(channelKey)) {
					return
				}

				onContextMenu(event, channelKey)
			})
		} else {
			ticks.on("contextmenu", null)
		}

		ticks.select("text")
			.text(channelKey => {
				const info = infoByKey.get(channelKey)
				return info?.label ?? channelKey
			})
			.style("fill", channelKey => {
				if (hiddenKeys.has(channelKey)) {
					return this.hiddenColor
				}

				const info = infoByKey.get(channelKey)

				if (info?.type === "ecg") {
					return LABEL_SETTINGS[info.key]?.color ?? this.ecgVisibleColor
				}

				return this.visibleColor
			})
			.attr("opacity", channelKey => {
				return hiddenKeys.has(channelKey) ? 0.55 : 1
			})
			.attr("font-style", channelKey => {
				return hiddenKeys.has(channelKey) ? "italic" : null
			})
	}
}
